import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ClipboardList, ChevronDown, Loader2, Sparkles } from 'lucide-react';
import api from '../api';

/**
 * Compact dropdown for teachers listing classes that still need feedback.
 * Clicking a row calls `onOpen(classId)` so the parent can pop the
 * ClassFeedbackForm dialog.
 */
export default function PendingFeedbackDropdown({ onOpen }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/feedback/pending');
      setItems(res.data.pending || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const count = items.length;

  return (
    <div className="relative" ref={ref} data-testid="pending-feedback-widget">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 transition text-xs font-bold text-amber-800"
        data-testid="pending-feedback-btn"
      >
        <ClipboardList className="w-4 h-4" />
        <span className="hidden sm:inline">Pending Feedback</span>
        {count > 0 && (
          <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center" data-testid="pending-feedback-badge">
            {count > 9 ? '9+' : count}
          </span>
        )}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[420px] bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden z-50" data-testid="pending-feedback-dropdown">
          <div className="px-3 py-2 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-slate-100 flex items-center justify-between">
            <p className="text-xs font-bold text-slate-900">Classes needing feedback</p>
            <span className="text-[10px] text-slate-500 font-semibold">{count} pending</span>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {loading && items.length === 0 && (
              <div className="text-center py-6 text-xs text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="flex flex-col items-center py-8 text-slate-400">
                <Sparkles className="w-6 h-6 mb-1" />
                <p className="text-xs">All caught up!</p>
              </div>
            )}
            {items.map(it => (
              <button
                key={it.class_id}
                onClick={() => { setOpen(false); onOpen?.(it.class_id); }}
                className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-slate-50 border-b border-slate-100 transition"
                data-testid={`pending-feedback-item-${it.class_id}`}
              >
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <ClipboardList className="w-3.5 h-3.5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-900 truncate">{it.student_name || 'Student'}</p>
                  <p className="text-[11px] text-slate-500 truncate">{it.date} · {it.time || '—'} · {it.course || 'Class'}</p>
                  {it.topic_name && <p className="text-[10px] text-indigo-600 font-semibold truncate">{it.topic_name}</p>}
                </div>
                {it.has_draft && (
                  <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">Draft</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
